import { group } from 'radash';

/**
 * P2P Dowloads are slightly different.
 *
 * All P2P admissions + reservations for one passenger are already grouped to the same "composite QR code" document.
 * So we do not group P2P downloads per product, but they are al grouped together in one group.
 */
export function transformTicketGroupsP2P(bookingParts: ApiBookingPart[], response: ApiSchema['BookingResponse']) {
    const apiBooking = response?.booking;
    if (!apiBooking) {
        return [];
    }

    // One static "P2P" ticket group for all P2P downloads.
    const ticketGroup = {
        id: 'p2p',
        type: 'P2P',
        tickets: [] as TicketP2P[],
    } satisfies TicketGroupP2P;

    // List is based on api booking "documents" of type COMPOSITE_QR_CODE. Because each passenger has 1 document, regardless
    // of how many fulfillments (reservations/admissions) he has. (In contrast to activities/accommodations where 1 document = 1 fulfillment)
    const ticketDocuments = apiBooking.documents?.filter(d => d.type === 'COMPOSITE_QR_CODE') ?? [];
    const passengerDocuments = group(ticketDocuments, d => d.passengerRef ?? '');

    // Create one ticket per passenger/document. We match all admissions and reservations and do
    // get the matching trips from there. This is then used to properly display trip destination etc. on the download
    ticketGroup.tickets = Object
        .entries(passengerDocuments)
        .map<TicketP2P>(([passengerRef, documentItems]) => {
            // Get matching admissions for this passenger.
            const admissions = bookingParts
                .filter(bp => bp.objectType === 'Admission')
                .filter(bp => bp.passengerIds.includes(passengerRef));
                // Get matching reservations for this passenger.
            const reservations = bookingParts
                .filter(bp => bp.objectType === 'Reservation')
                .filter(bp => bp.passengerIds.includes(passengerRef));

            // Include trips that match the passenger's reservations/admissions
            const trips = apiBooking.trips
                ?.filter(t => apiBooking.activeTripIds?.includes(t.id))
                ?.filter((t) => {
                    const hasMatchingReservations = reservations.filter(r => r.status !== 'REFUNDED').some(r => r.tripCoverage?.coveredTripIds?.includes(t.id));
                    const hasMatchingAdmissions = admissions.filter(a => a.status !== 'REFUNDED').some(a => a.tripCoverage?.coveredTripIds?.includes(t.id));
                    return hasMatchingReservations || hasMatchingAdmissions;
                })
                ?.sort((a, b) => zurichDate(a?.startTime).getTime() - zurichDate(b?.startTime).getTime()) || [];

            return transformTicketP2P(
                passengerRef,
                documentItems ?? [],
                admissions,
                reservations,
                trips,
                apiBooking,
            );
        })
        // Sort P2P tickets by trip departure time, then by passenger name.
        ?.sort((a, b) => {
            const departureTimeCompare = (a?.trips?.[0]?.departureTime?.getTime() ?? 0) - (b?.trips?.[0]?.departureTime?.getTime() ?? 0);
            if (departureTimeCompare !== 0) {
                return departureTimeCompare;
            }
            return (a?.summaryLine1 ?? '').localeCompare(b?.summaryLine1 ?? '');
        });

    return [ticketGroup];
}
